import {InjectionToken, REQUEST_CONTEXT} from '@angular/core';

export interface SsrRequestContext {
  origin: string;
  cookieHeader: string;
}

export const SSR_REQUEST_CONTEXT = REQUEST_CONTEXT as InjectionToken<SsrRequestContext | null>;

const SSR_REQUEST_CONTEXT_KEY = '__mbnmSsrRequestContext';

export function runWithSsrRequestContext<T>(context: SsrRequestContext, callback: () => T): T {
  const globalContext = globalThis as typeof globalThis & {
    [SSR_REQUEST_CONTEXT_KEY]?: SsrRequestContext;
  };
  const previousContext = globalContext[SSR_REQUEST_CONTEXT_KEY];
  globalContext[SSR_REQUEST_CONTEXT_KEY] = context;

  const clearContext = () => {
    if (previousContext) {
      globalContext[SSR_REQUEST_CONTEXT_KEY] = previousContext;
    } else {
      delete globalContext[SSR_REQUEST_CONTEXT_KEY];
    }
  };

  const zone = getZone();
  try {
    const result = zone?.current?.fork
      ? zone.current.fork({
        name: 'mbnm-ssr-request',
        properties: {
          [SSR_REQUEST_CONTEXT_KEY]: context
        }
      }).run(callback)
      : callback();
    if (isPromiseLike(result)) {
      return result.finally(clearContext) as T;
    }

    clearContext();
    return result;
  } catch (error) {
    clearContext();
    throw error;
  }
}

export function getCurrentSsrRequestContext(): SsrRequestContext | null {
  return getZone()?.current?.get?.(SSR_REQUEST_CONTEXT_KEY) ?? getGlobalSsrRequestContext();
}

function getGlobalSsrRequestContext(): SsrRequestContext | null {
  return ((globalThis as typeof globalThis & {
    [SSR_REQUEST_CONTEXT_KEY]?: SsrRequestContext;
  })[SSR_REQUEST_CONTEXT_KEY]) ?? null;
}

function getZone(): any {
  return (globalThis as typeof globalThis & { Zone?: any }).Zone;
}

function isPromiseLike<T>(value: T | Promise<T>): value is Promise<T> {
  return !!value && typeof (value as Promise<T>).finally === 'function';
}
